import hash from "object-hash";
import { buildFullReloadPropertyCollection, buildPropertyCollection } from "./build-property-collection";
import "./epiEditMode.scss";

interface EventHandle {
    remove: () => void;
}

const on = (target: Node, eventType: string, listener: any, capture = false) => {
    // summary:
    //      Simple event handler registration. Returns a handle with a remove method.
    // target: Object
    //      the object to listen for events on. Must implement addEventListener.
    // eventType: String
    //      The message payload to send to the the topic listeners
    // listener: function
    //      The function to execute when the event is triggered

    const resolvedCapture = !!capture;

    target.addEventListener(eventType, listener, resolvedCapture);

    return {
        remove: function () {
            target.removeEventListener(eventType, listener, resolvedCapture);
            // @ts-ignore
            target = eventType = listener = capture = null;
        },
    };
};

(function (scope: any, global: any) {
    // summary:
    //      Handles integration between site content in preview and the edit UI.
    // tags:
    //      private

    /* global self: true */

    "use strict";

    let keyPrefix = "epi::",
        messageHandlers: any = {},
        eventHandles: EventHandle[] = [],
        name = scope.name || "";

    function publish(topic: string, message: any) {
        // summary:
        //      Publishes a message using the postMessage hub.
        // topic: String
        //      The name of the topic to publish to
        // message: Object
        //      The message payload to send to the the topic listeners

        global.postMessage({ id: name + topic, message: message }, "*");
    }

    function subscribe(topic: string, handler: any) {
        // summary:
        //      Subscribes to a message on the postMessage hub.
        // topic: String
        //      The topic to subscribe to
        // handler: Function
        //      A function to call when a message is published to the given topic

        let key = keyPrefix + topic;

        messageHandlers[key] ? messageHandlers[key].push(handler) : (messageHandlers[key] = [handler]);

        return {
            remove: function () {
                let i,
                    topicHandlers = messageHandlers[key];

                if (topicHandlers) {
                    while ((i = topicHandlers.indexOf(handler)) !== -1) {
                        topicHandlers.splice(i, 1);
                    }
                }
                // @ts-ignore
                topic = handler = key = null;
            },
        };
    }

    function ready(fn: () => void) {
        // summary:
        //      Executes the provided function when the dom is ready.
        // fn: function
        //      The callback to execute when the dom is ready.

        let loadHandle: any;

        if (!fn) {
            return;
        }

        function load() {
            if (scope.document.readyState === "complete") {
                loadHandle.remove();
                fn();
                // @ts-ignore
                loadHandle = fn = null;
            }
        }

        if (scope.document.readyState === "complete") {
            fn();
        } else {
            loadHandle = on(scope.document, "readystatechange", load);
        }
    }

    function destroy() {
        eventHandles.forEach(function (handle) {
            handle.remove();
        });

        delete scope.epi.publish;
        delete scope.epi.subscribe;
        delete scope.epi.inEditMode;
        delete scope.epi.isEditable;
        delete scope.epi.ready;
        delete scope.epi.beta.inEditMode;
        delete scope.epi.beta.isEditable;
        delete scope.epi.beta.ready;
        delete scope.epi.beta;

        // @ts-ignore
        scope = global = messageHandlers = eventHandles = null;
    }

    function initSizeListener() {
        let size: any;

        function getElement(): HTMLElement | null {
            let doc = scope.document,
                elm = doc.getElementById("epi-bodysize");

            if (elm) {
                if (doc.body.lastChild === elm) {
                    return elm;
                }
                if (elm.parentNode) {
                    elm.parentNode.removeChild(elm);
                }
                elm = null;
            }

            doc.body.style.overflow = "hidden";
            doc.body.insertAdjacentHTML(
                "beforeend",
                "<div id='epi-bodysize' style='position: absolute; padding: 0; margin: 0;height: 0; width: 100%;'></div>"
            );
            return doc.getElementById("epi-bodysize");
        }

        function sizeChecker() {
            const elm = getElement();
            if (!elm) {
                return;
            }
            const w = elm.offsetWidth;
            const h = elm.offsetTop;

            if (!size || size.w !== w || size.h !== h) {
                size = { h: h, w: w };
                // include new size of properties when size has changed.
                size.properties = buildPropertyCollection();
                publish("/site/resize", size);
            }
        }

        eventHandles.push(subscribe("/site/checksize", sizeChecker));
    }

    function thumbnailsSyncListeners() {
        eventHandles.push(
            subscribe("LastFileUploadedAndReplaced", function () {
                scope.document.querySelectorAll("img").forEach((img) => {
                    if (!img.src) {
                        return;
                    }

                    img.src = img.src + "#" + new Date().getTime();
                });
            })
        );
    }

    // The user profile is not available in this context, so we just scope to `epi`.
    function initData() {
        const exports = (scope.epi = scope.epi || {});

        exports.beta = {
            // The "epieditmode" URL query is True when in edit mode and editing, and False when in edit mode and previewing, so it's presence means the view is in edit mode.
            inEditMode: scope.document.location.search.indexOf("epieditmode") >= 0,
            isEditable: false,
            ready: false,
        };

        // The "epieditmode" URL query is True when in edit mode and editing, and False when in edit mode and previewing, so it's presence means the view is in edit mode.
        exports.inEditMode = scope.document.location.search.indexOf("epieditmode") >= 0;
        exports.isEditable = false;
        exports.ready = false;

        function setProperties(initialData: any) {
            exports.beta.isEditable = initialData.isEditable;
            exports.beta.ready = true;

            exports.isEditable = initialData.isEditable;
            exports.ready = true;
        }

        eventHandles.push(subscribe("epiReady", setProperties));
        thumbnailsSyncListeners();
    }

    function initMessageHandler() {
        function handleMessage(msg: any) {
            let handlers,
                msgData = msg.data;

            if (msgData && msgData.id) {
                handlers = messageHandlers[keyPrefix + msgData.id];

                handlers &&
                    handlers.forEach(function (handler: any) {
                        try {
                            handler(msgData.data);
                        } catch (ex) {
                            // eslint-disable-line no-empty
                        }
                    });

                msgData = null;
            }
        }

        eventHandles.push(on(scope, "message", handleMessage));
    }

    function exposeMethods() {
        const exports = (scope.epi = scope.epi || {});

        exports.publish = publish;
        exports.subscribe = subscribe;
    }

    let _lastPropertiesCollection = "_";

    function publishPropertiesEventIfNeeded() {
        const properties = buildPropertyCollection();
        const currentProperties = hash(properties);
        if (_lastPropertiesCollection === currentProperties) {
            return;
        }
        _lastPropertiesCollection = currentProperties;

        const fullReloadProperties = buildFullReloadPropertyCollection();
        publish("/site/properties", {
            properties: properties,
            clientHeight: scope.document.body.clientHeight,
            fullReloadProperties: fullReloadProperties,
        });
    }

    function setupObserver() {
        let deferredHandle: any | null = null;

        const targetNode = scope.document.body;
        const config = { attributes: true, childList: true, subtree: true };
        const callback = function () {
            if (deferredHandle) {
                clearTimeout(deferredHandle);
            }
            deferredHandle = setTimeout(() => {
                publishPropertiesEventIfNeeded();
            }, 500);
        };
        const observer = new MutationObserver(callback);
        observer.observe(targetNode, config);

        // resize observer
        const resizeObserver = new ResizeObserver(callback);
        resizeObserver.observe(targetNode);
    }

    ready(function () {
        let message = { url: scope.location.href };

        initMessageHandler();
        initSizeListener();
        initData();
        exposeMethods();

        eventHandles.push(
            on(scope, "unload", function () {
                publish("/site/unload", message);
                destroy();
                // @ts-ignore
                message = null;
            })
        );

        eventHandles.push(
            on(scope, "mouseup", function () {
                publish("/site/mouseup", message);
            })
        );

        publish("/site/load", message);
        // Collect metadata about all the properties and publish it so that Edit view can build a copy based upon this metadata
        publishPropertiesEventIfNeeded();
    });

    setTimeout(() => {
        setupObserver();
    }, 0);
})(self, top);
